import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'circular' | 'rectangular';
  width?: string | number;
  height?: string | number;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'rectangular',
  width,
  height,
}) => {
  const getVariantClass = () => {
    switch (variant) {
      case 'circular':
        return 'rounded-full';
      case 'text':
        return 'rounded-md h-4';
      case 'rectangular':
      default:
        return 'rounded-xl';
    }
  };

  const style: React.CSSProperties = {
    width: width,
    height: height,
  };

  return (
    <div
      style={style}
      className={`animate-pulse bg-slate-200 dark:bg-slate-800 ${getVariantClass()} ${className}`}
    />
  );
};

export const SkeletonTable: React.FC<{ rows?: number; columns?: number }> = ({
  rows = 5,
  columns = 4,
}) => {
  return (
    <div className="w-full bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 p-6 space-y-4">
      <div className="flex justify-between items-center pb-4 border-b border-slate-100 dark:border-slate-800">
        <Skeleton variant="text" width="200px" height="24px" />
        <Skeleton variant="rectangular" width="120px" height="36px" />
      </div>
      <div className="space-y-3">
        {Array.from({ length: rows }).map((_, rIndex) => (
          <div key={rIndex} className="flex gap-4 items-center">
            {Array.from({ length: columns }).map((_, cIndex) => (
              <Skeleton key={cIndex} variant="rectangular" className="flex-1" height="32px" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

export const SkeletonCard: React.FC<{ count?: number }> = ({ count = 3 }) => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
      {Array.from({ length: count }).map((_, idx) => (
        <div
          key={idx}
          className="bg-white dark:bg-slate-900 p-6 rounded-2xl border border-slate-200 dark:border-slate-800 space-y-4 shadow-sm"
        >
          <div className="flex justify-between items-center">
            <Skeleton variant="text" width="120px" height="18px" />
            <Skeleton variant="circular" width="36px" height="36px" />
          </div>
          <Skeleton variant="text" width="160px" height="32px" />
          <Skeleton variant="text" width="100%" height="14px" />
        </div>
      ))}
    </div>
  );
};
